/**
 * Shared LLM Service Utilities
 * Provider-agnostic helpers: response parsing, prompt building, key management.
 * Single source of truth for getProvider()/getGeminiKey()/getClaudeKey()/etc —
 * every 4-provider service (RiffWriter, Narrative Engine, Story Engine, Genre DNA,
 * Riff DNA, Lyric Orchestrator/Polish, Vault Enrich, Suno Readiness, Prompt Doctor,
 * llmDispatcher.ts) imports these instead of redefining them locally. AUTO-022:
 * these getters used to be copy-pasted into each file, which is how RiffWriter's
 * Hybrid Mode silently missed the provider-selector branch entirely — a single
 * shared definition can't be silently skipped the same way.
 * geminiService.ts remains untouched — it has its own slightly different key
 * lookup (personal-key vs free-trial handling) that isn't a drop-in match.
 */

import { HOOKHOUSE_PRO_SYSTEM_PROMPT, STYLE_PRESETS, MUSIC_CRITIC_SYSTEM_PROMPT } from '@/constants';
import { HookhouseFormData, ParsedSunoOutput, MusicCritique, SunoClip } from '@/types';
import type { PersonaOverride } from '@/types/persona.types';
import { renderLockedStyleTemplate } from '@/shared/personaLock';
import { trimLyricsBodyToHardCap } from '@/shared/lyricsHardCap';
import { SUMMARY_RULE } from '@/shared/summaryRules';
import { SUNO_LYRICS_INJECTION_LINES } from '@/shared/sunoLyricsInjection';
import { buildRhymeInstructionText, buildStructureInstructionText } from '@/utils/rhymeStructureInstructions';

// ============================================
// Provider & Key Getters
// ============================================

export type LLMProvider = 'gemini' | 'claude' | 'openai' | 'xai';

export function getProvider(): LLMProvider {
  return (localStorage.getItem('llm_provider') as LLMProvider) || 'gemini';
}

export function getClaudeKey(): string {
  return localStorage.getItem('anthropic_api_key') || '';
}

export function getClaudeProxy(): string {
  return localStorage.getItem('anthropic_proxy_url') || import.meta.env.VITE_ANTHROPIC_PROXY_URL || 'https://anthropic-proxy.frobinson47.workers.dev';
}

export function getClaudeModel(): string {
  return localStorage.getItem('claude_model') || 'claude-sonnet-4-5-20250929';
}

export function getGeminiKey(): string {
  return localStorage.getItem('gemini_api_key') || '';
}

export function getGeminiModel(): string {
  // gemini-2.5-flash was sunset by Google (confirmed live, 404s "no longer
  // available to new users") -- gemini-3-flash-preview is this app's own
  // existing "Fast" tier option (README/SettingsModal), confirmed still
  // working and faster than Google's own suggested replacement
  // (gemini-3.6-flash) in a live side-by-side test.
  return localStorage.getItem('gemini_model') || 'gemini-3-flash-preview';
}

export function getOpenAIKey(): string {
  return localStorage.getItem('openai_api_key') || '';
}

export function getOpenAIModel(): string {
  return localStorage.getItem('openai_model') || 'gpt-4o';
}

export function getXAIKey(): string {
  return localStorage.getItem('xai_api_key') || '';
}

export function getXAIModel(): string {
  return localStorage.getItem('xai_model') || 'grok-4-1-fast-reasoning';
}

// ============================================
// Response Parsing (identical to geminiService)
// ============================================

/**
 * Trim a comma-separated tag list to fit within `max` chars without cutting
 * a tag mid-word — drops whole trailing tags instead. Used because the LLM's
 * stated "1,000 char hard limit" for style/excluded-style tags is not
 * reliably respected (genre-blended briefs especially tend to overrun), and
 * unlike lyrics there was previously no corrective pass — the overage just
 * flowed straight into storage and got rejected by the vault's save schema.
 */
export const trimTagList = (tags: string, max: number): string => {
  if (tags.length <= max) return tags;
  const segments = tags.split(',').map(s => s.trim()).filter(Boolean);
  const kept: string[] = [];
  let length = 0;
  for (const segment of segments) {
    const addedLength = length === 0 ? segment.length : length + 2 + segment.length;
    if (addedLength > max) break;
    kept.push(segment);
    length = addedLength;
  }
  return kept.join(', ');
};

/**
 * Resolve the Style tags actually sent to Suno, falling back to the prose
 * `styleBlueprint` field when `style` (SUNO TAGS) came back empty. Every
 * call site that did `style || styleBlueprint` inline used the fallback
 * uncapped — styleBlueprint has no length enforcement of its own (it's
 * meant as free prose, not a 1000-char tag list), so when a brief stacks
 * multiple blend layers (vocal + production + solo) the LLM's narration of
 * all three into that field routinely runs several thousand characters,
 * and an empty `style` meant that uncapped prose went straight to Suno.
 */
export const resolveStyleTags = (
  output: { style?: string; styleBlueprint?: string },
  max: number = 1000,
): string => trimTagList(output.style || output.styleBlueprint || '', max);

/**
 * Build the browser-console script pasted on suno.com/create to auto-fill
 * lyrics/style/title and the intelligence sliders. Also doubles as the only
 * way to renew Suno's session/captcha when it times out, so every generation
 * pipeline that ends in a Suno song (RiffWriter, Album Mode, Studio Team)
 * should keep this around on its output rather than discarding it -- losing
 * it means burning a whole extra generation just to get a fresh one.
 *
 * Several callers (HHHistoryTab, StudioTeamPanel, Album Mode's take
 * previews, geminiService, this file's own parseResponse) call this
 * directly on lyrics that were never pre-capped by a caller-side hard-cap
 * pass -- for those, the line-safe trim below IS the only truncation this
 * text ever gets, so it has to be the one doing it rather than a blind
 * substring. A blind `.substring(0, 5000)` here can land mid-word/mid-line
 * even on a body a caller already hard-capped to a similar length (a
 * protected-content floor can legitimately land just over 5000 -- see
 * src/shared/lyricsHardCap.ts) -- so this must always drop on a line
 * boundary too, never just re-chop what was already capped correctly.
 */
export function buildSunoInjectionScript(
  title: string,
  style: string,
  lyrics: string,
  weirdness: number,
  styleStrength: number,
): string {
  const machineStyle = (style || '').substring(0, 1000).trim();
  const machineLyrics = trimLyricsBodyToHardCap(lyrics || '', 5000, 4920).body.trim();

  return [
    '// --- Hookhouse Pro Automation Terminal ---',
    '// Paste this while on suno.com/create in "Custom Mode"',
    '',
    'function setNativeValue(element, value) {',
    '    if (!element) return;',
    '    const valueSetter = Object.getOwnPropertyDescriptor(element.constructor.prototype, "value")?.set;',
    '    const prototypeValueSetter = Object.getOwnPropertyDescriptor(Object.getPrototypeOf(element), "value")?.set;',
    '    if (valueSetter && valueSetter !== prototypeValueSetter) {',
    '        valueSetter.call(element, value);',
    '    } else if (valueSetter) {',
    '        valueSetter.call(element, value);',
    '    }',
    '    element.dispatchEvent(new Event("input", { bubbles: true }));',
    '}',
    '',
    ...SUNO_LYRICS_INJECTION_LINES,
    '',
    'function adjustSlider(sliderLabel, targetValue) {',
    '    const slider = document.querySelector(`div[role="slider"][aria-label*="${sliderLabel}"]`);',
    '    if (slider) {',
    '        slider.focus();',
    '        const pressKey = (key) => slider.dispatchEvent(new KeyboardEvent("keydown", { key, code: key, bubbles: true, cancelable: true }));',
    '        const intervalId = setInterval(() => {',
    '            const currentVal = parseFloat(slider.getAttribute("aria-valuenow"));',
    '            if (Math.abs(currentVal - targetValue) < 1) { clearInterval(intervalId); return; }',
    '            if (currentVal > targetValue) pressKey("ArrowLeft"); else if (currentVal < targetValue) pressKey("ArrowRight");',
    '        }, 10);',
    '    }',
    '}',
    '',
    'console.log("🚀 Injecting Hookhouse Session Architecture...");',
    '',
    'injectLyrics(' + JSON.stringify(machineLyrics) + ');',
    '',
    'const styleInput = document.querySelector(\'textarea[maxlength="1000"]\');',
    'if (styleInput) { setNativeValue(styleInput, ' + JSON.stringify(machineStyle) + '); console.log("✓ Style tags injected"); } else { console.warn("⚠ Style field not found — Suno may have changed their page again"); }',
    '',
    'const songTitleInput = document.querySelector(\'input[placeholder*="Song Title"]\');',
    'if (songTitleInput) { setNativeValue(songTitleInput, ' + JSON.stringify(title) + '); console.log("✓ Title injected"); } else { console.warn("⚠ Title field not found — Suno may have changed their page again"); }',
    '',
    '// Set Intelligence Sliders',
    'adjustSlider("Weirdness", ' + weirdness + ');',
    'adjustSlider("Style Influence", ' + styleStrength + ');',
    '',
    'console.log("%c Injection Complete! Ready to Create. ", "background: rgb(var(--color-accent)); color: #000; font-size: 14px; font-weight: bold;");'
  ].join('\n');
}

export const extractBlock = (text: string, label: string): string => {
  // Labels may come back wrapped in markdown bold ("**SUMMARY:**", "**SUMMARY**:"),
  // which Sonnet does now and then — without this the whole blueprint was
  // rejected as malformed (AUTO-051).
  const bold = '(?:\\*\\*|__)?';
  const hasColon = label.trimEnd().endsWith(':');
  const core = (hasColon ? label.trimEnd().slice(0, -1) : label).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const escapedLabel = `${bold}${core}${hasColon ? `[ \\t]*${bold}[ \\t]*:` : ''}${bold}`;
  const strictRegex = new RegExp(`(?:^|\\n)(?:#*\\s*)?${escapedLabel}\\s*\\n*\\s*\\\x60\\\x60\\\x60(?:[\\w-]*\\n)?([\\s\\S]*?)\\\x60\\\x60\\\x60`, 'i');
  const strictMatch = text.match(strictRegex);
  if (strictMatch && strictMatch[1].trim()) return strictMatch[1].trim();

  const sectionKeywords = "TITLE|SUMMARY|STYLE|SUNO|EXCLUDED|LYRICS";
  const looseRegex = new RegExp(`(?:^|\\n)(?:#*\\s*)?${escapedLabel}\\s*\\n*([\\s\\S]*?)(?=\\n(?:#*\\s*)?${bold}(?:${sectionKeywords})|$)`, 'i');
  const looseMatch = text.match(looseRegex);
  if (looseMatch && looseMatch[1].trim()) return looseMatch[1].replace(/\x60\x60\x60[\w-]*|\x60\x60\x60/g, '').trim();
  return "";
};

export const parseResponse = (responseText: string, styleStrength: number = 85, weirdness: number = 65): ParsedSunoOutput => {
  const fullResponse = responseText;
  const titleArtist = extractBlock(fullResponse, 'TITLE / ARTIST:');
  const summary = extractBlock(fullResponse, 'SUMMARY:');
  const styleBlueprint = extractBlock(fullResponse, 'STYLE (PRODUCTION BLUEPRINT):');
  const style = extractBlock(fullResponse, 'SUNO TAGS:');
  const excludedStyle = extractBlock(fullResponse, 'EXCLUDED STYLES:');
  const lyricsWithTags = extractBlock(fullResponse, 'LYRICS:');

  if (!titleArtist || !summary || !styleBlueprint || !lyricsWithTags) {
    throw new Error(`The session architect returned a malformed blueprint. Please kick the jukebox and try again.`);
  }

  // Split title // artist with fallback separators: //, /, —, –, " - "
  const titleSep = titleArtist.includes('//') ? '//'
    : titleArtist.includes(' — ') ? ' — '
    : titleArtist.includes(' – ') ? ' – '
    : titleArtist.includes(' - ') ? ' - '
    : '/';
  const parts = titleArtist.split(titleSep).map(s => s.trim());
  const parsed: ParsedSunoOutput = {
    titleArtist, summary, styleBlueprint, style, excludedStyle, lyricsWithTags,
    title: parts[0] || "Untitled", artist: parts[1] || "Unknown Artist",
    lyricsAlone: (lyricsWithTags || "").replace(/\{LAYER-1 VOCAL PHYSICS\}[\s\S]*?\n\n/g, '').replace(/\[.*?\]\n?/g, '').trim(),
    javascriptCode: "", fullResponse,
    styleStrength, weirdness
  };

  // Track character limits for Suno V5. The LLM is told to stay under these,
  // but genre-blended briefs especially tend to overrun the stated limit
  // (unlike lyrics, there was previously no corrective trim for tags, so the
  // overage flowed straight into storage and got rejected by the vault's
  // save schema) — trim here so the stored value matches what Suno actually
  // receives instead of silently exceeding it.
  const rawStyleLen = (parsed.style || '').length;
  const rawExcludeLen = (parsed.excludedStyle || '').length;
  if (rawStyleLen > 1000) {
    console.warn(`⚠️ [LLM] Style tags exceed Suno limit: ${rawStyleLen}/1000 chars — trimming to whole tags`);
    parsed.style = trimTagList(parsed.style || '', 1000);
  }
  if (rawExcludeLen > 1000) {
    console.warn(`⚠️ [LLM] Excluded styles exceed Suno limit: ${rawExcludeLen}/1000 chars — trimming to whole tags`);
    parsed.excludedStyle = trimTagList(parsed.excludedStyle || '', 1000);
  }

  const styleLen = (parsed.style || '').length;
  const excludeLen = (parsed.excludedStyle || '').length;
  const lyricsLen = (parsed.lyricsWithTags || '').length;
  parsed.charLimits = {
    styleTags: { count: styleLen, max: 1000, over: styleLen > 1000 },
    excludedStyles: { count: excludeLen, max: 1000, over: excludeLen > 1000 },
    lyrics: { count: lyricsLen, max: 5000, over: lyricsLen > 5000 },
  };

  if (parsed.charLimits.lyrics.over) {
    console.warn(`⚠️ [LLM] Lyrics exceed Suno limit: ${lyricsLen}/5000 chars — will be truncated for console injection`);
  }

  parsed.javascriptCode = buildSunoInjectionScript(
    parsed.title,
    resolveStyleTags(parsed),
    parsed.lyricsWithTags || '',
    weirdness,
    styleStrength,
  );

  return parsed;
};

// ============================================
// Prompt Builders
// ============================================

export const IMAGE_BLUEPRINT_SYSTEM_PROMPT = `Output a single structured JSON object capturing visual, spatial, semantic, and atmospheric data for a 5:7 album cover.

[STYLE ATLAS — mandatory: pick ONE named style by name, do NOT freehand]

Source: Image Style Atlas v1 (ukslim "Poster Prompts," 100-style catalogue ingested, de-postered, treatment layer only). Pick ONE style by name from the Quick-Pick Index below and let its fields drive the JSON.

FIELD MAPPING (Atlas → JSON):
- Atlas PALETTE → global_scene.style.palette
- Atlas MOOD → global_scene.lighting.overall_mood (closest of: naturalistic|cinematic|noir|high_key|low_key) AND global_scene.style.mood (verbatim Atlas line)
- Atlas TRAITS + REFERENCES → global_scene.style.atlas_id, atlas_name, traits, references[]
- Atlas TRAITS (figurative motifs) → objects[] with concrete labels
- Atlas EXCLUDE → global_scene.style.excludes[] (image-side negative conditioning)
- Atlas WORDMARK/TYPE brief → typography.title.treatment AND typography.artist.treatment

CRAFT RULES:
1. Kill unwanted aging on historical styles — append "vivid saturated colour, clean reproduction, high contrast, no aging or distressing" to global_scene.style.traits unless decay is intentional.
2. Control invention: if no people belong on this cover, set people: [] AND add "no photographs of people, abstract graphic elements only" to global_scene.style.excludes.
3. Honesty: don't depict instruments, venues, or scenes the song doesn't earn. The cover is a promise.
4. ONE style per cover. Do not stack atlas styles — picks become noise.
5. Self-cancellation check: do not put a vector in excludes[] that style.traits is requesting (e.g., don't exclude "bold colour" while picking a maximalist palette).

DEFAULT PICKS FOR RECURRING VOICES (override only on a real reason):
- Southern Americana / Grease-Stained Gospel → primary neon_sign; alternates screenprint_pop, halftone_newsprint, etching_engraving, botanical_scientific
- Scooby VanZant / Southern Groove Metal → primary heavy_metal_poster; alternates grunge_typography, brutalist_graphic, expressionist_woodcut
- Dark Cinematic → primary german_expressionist_film; alternates polish_poster_school, monochrome_single_accent, penny_dreadful
- Psychedelic / jam → primary psychedelic_sixties; alternates synthwave, vaporwave

Sparse-input rule: if the song gives no visual direction, pick the project default and commit. Do not present a menu.

QUICK-PICK INDEX (100 styles — name, era, mood, core palette):
1. Arts and Crafts (1880s–1910s, earnest) — forest green, terracotta, ochre, deep burgundy, cream
2. Art Nouveau (1890s–1910s, elegant) — muted gold, sage, dusty rose, teal, cream
3. Art Deco (1920s–1930s, glamorous) — black, gold, cream, deep teal, coral
4. Streamline Moderne (1930s–1940s, optimistic) — chrome silver, navy, white, red accent, sky blue
5. Bauhaus / Modernist (1920s–1930s, cultural) — primary red, blue, yellow, black, white
6. Swiss / International Typographic (1950s–1970s, institutional) — white, black, one accent
7. Constructivist (1920s–1930s, urgent) — red, black, white, occasional grey
8. De Stijl (1917–1930s, intellectual) — red, blue, yellow, black, white only
9. Mid-century Modern Graphic (1940s–1960s, optimistic) — teal, mustard, coral, avocado
10. Memphis / 80s Postmodern (1980s, fun) — hot pink, electric blue, yellow, black, white
11. Brutalist Graphic Design (1960s–present, rebellious) — black, white, one brutal accent
12. Contemporary Editorial (2000s–present, premium) — white, black, one editorial accent
13. Wayfinding / Signage (1960s–present, functional) — white, dark grey, safety green
14. Vienna Secession (1897–1914, jewelled) — black, white, gold, grey-violet
15. Glasgow Style (1893–1913, cool) — black, white, rose pink, grey-purple
16. Push Pin Studios (1954–1990s, witty) — flat vivid — cobalt, vermillion, lime
17. New Wave Typography (1976–1990, intellectual) — black, white, warm grey, strong accents
18. Grunge Typography (1990–1998, raw) — black, white, photographic tones
19. Japonisme (1870s–1900s, refined) — indigo, vermillion, cream, black, muted gold
20. Victorian Broadside (1850s–1900s, boisterous) — black, cream, deep red, gold, forest green
21. Belle Époque Lithograph (1890s–1910s, lively) — warm red, yellow, black, teal, cream
22. Futurist (1909–1940s, aggressive) — red, black, grey, yellow, white
23. Dada Collage (1916–1920s, absurdist) — black, white, newsprint grey, red accent
24. Cubist Exhibition Poster (1907–1920s, intellectual) — ochre, grey, umber, black, cream
25. Surrealist (1920s–1950s, uncanny) — deep blue sky, warm sand, muted green, ochre
26. Expressionist Woodcut (1905–1920s, raw) — black, white, one accent — red or orange
27. Pop Art (1950s–1960s, loud) — red, yellow, blue, black, white
28. Op Art (1960s, hypnotic) — black, white, occasional single accent
29. Psychedelic Sixties (1966–1970s, trippy) — hot pink, orange, purple, lime green
30. Minimalism Art (1960s–1970s, contemplative) — white, off-white, one colour
31. Graffiti / Street Art (1970s–present, urban) — electric colours on dark or brick
32. Suprematism (1915–1925, pure) — black, red, white
33. Vorticism (1914–1920, aggressive) — black, white, sharp red or yellow
34. Neue Sachlichkeit (1920s–1930s, urgent) — black, white, photographic grey
35. Orphism / Simultanism (1912–1920s, joyful) — full spectrum
36. Concrete Art (1930s–1970s, intellectual) — systematic colour sequences
37. CoBrA (1948–1951, raw) — vivid unmodulated — cobalt, vermillion, yellow
38. Pre-Raphaelite Illustration (1848–1900, romantic) — jewel tones — deep red, emerald, cobalt
39. Big Eyes / Keane Waif (1950s–1970s, sentimental) — muted warm — ochre, sienna, umber
40. Risograph (1980s–present, indie) — fluorescent pink, teal, yellow, black
41. Stamp / Letterpress modernised (Contemporary, crafted) — black, cream, one accent
42. Screenprint Pop (1960s–present, direct) — bold opaque colours
43. Botanical Scientific Illustration (Contemporary, quiet) — black ink on cream, one accent
44. Cut Paper / Matisse (1940s–present, joyful) — bright blue, orange, green, pink
45. Monochrome + Single Accent (Contemporary, confident) — black, white, one accent
46. Cyanotype / Blueprint (1842–present, scientific) — Prussian blue and white only
47. Penny Dreadful (1840s–1890s, melodramatic) — black engraving on white, garish accent
48. Etching / Engraving (16th c.–present, authoritative) — black ink on cream
49. Halftone / Newsprint (1970s–1980s, urgent) — black ink on white, optional red
50. Mimeograph (1920s–1980s, community) — purple-blue or grey-blue on off-white
51. Child Poster Paint + Pro Typography (Contemporary, charming) — poster-paint primaries
52. School Cutout Collage (Contemporary, enthusiastic) — construction paper colours
53. Crayon Folk Handmade (Contemporary, warm) — crayon primaries
54. Punk Photocopied Fanzine (1970s–1980s, confrontational) — black, white, photocopy grey
55. Zine Cut-and-Paste (1990s–present, indie) — black and white with found colour
56. Mexican Calavera / Posada (Traditional, joyful) — vivid warm and cool
57. Pennsylvania Dutch / Fraktur (18th–19th c., heraldic) — barn red, cobalt, ochre
58. Madhubani / Indian Folk (Traditional, devotional) — ochre, terracotta, turmeric, indigo
59. Scandinavian Folk / Rosemaling (18th c.–present, warm) — earth tones
60. Polish Poster School (1950s–1980s, artistic) — deep red, ultramarine, saturated
61. Golden Age Travel Poster (1920s–1950s, aspirational) — azure, sand, sunset orange
62. Japanese Minimal Poster (1960s–present, calm) — white, black, one muted accent
63. Neon Sign / Diner Americana (1930s–1970s, nocturnal) — blue-black ground, neon pink/blue
64. East German DEFA Film Poster (1950s–1989, humanistic) — deep blues, reds, earthy tones
65. Taisho Modern (1912–1926, cosmopolitan) — deep red, black, gold, cream
66. Shanghai Calendar Poster / Yuefenpai (1920s–1940s, aspirational) — rouge, jade, warm tones
67. Festival of Britain (1951, optimistic) — red, white, blue, gold, bright green
68. Brazilian Tropicália (1967–1972, psychedelic) — tropical vivid
69. Italian Futurist Typography (1909–1944, loud) — black and white, strong accent
70. Soyuzmultfilm Animation (1950s–1980s, gentle) — dusty ochre, soft forest, warm grey
71. German Expressionist Film Poster (1919–1933, threatening) — black, white, cold grey
72. Hong Kong Cinema Poster (1950s–1990s, kinetic) — scarlet, gold, deep blue
73. Ukiyo-e / Japanese Woodblock (1600s–1900s, serene) — indigo, vermillion, black, cream
74. WPA / New Deal (1935–1943, civic) — red, blue, cream, black
75. Soviet Propaganda (1920s–1950s, heroic) — red, cream, black, gold
76. Cuban OSPAAAL Revolutionary (1964–1985, urgent) — flat black, red, one other
77. Atelier Populaire Paris May 1968 (urgent) — black on white or single flat colour
78. ACT UP / AIDS Crisis Activism (1987–1995, urgent) — black, one colour
79. North Korean Propaganda (triumphalist) — red, white, blue, yellow, maximally saturated
80. Soviet Space Programme (1957–1975, heroic-optimism) — deep space blue-black, silver, gold
81. 90s Rave / Acid Graphics (1988–1995, euphoric) — neon green, hot pink, electric blue, black
82. 90s Drum and Bass Flyer (1994–1999, dark) — black, dark blue, chrome silver, neon green
83. Festival Indie Lineup (2000s–present, contemporary) — depends on central graphic
84. Factory Records / Haçienda (1978–1992, austere) — black, white, one colour (often yellow)
85. 4AD Records (1980–2000s, gothic-romantic) — deep red, black, brown, bone white
86. Heavy Metal Concert Poster (1970s–present, dark) — black, deep red, orange flame, silver
87. Reggae / Sound System Flyer (1960s–1990s, warm) — red, gold, green, black
88. 80s NYC Hip Hop Flyer (1979–1995, direct) — black, two or three vivid primaries
89. Blaxploitation Movie Poster (1971–1975, cool) — burnt orange, rust, warm 1970s
90. Skateboard / Street Graphics (1980s–present, transgressive) — vivid clashing
91. Designers Republic (1986–present, subversive) — white, black, red, yellow, industrial grey
92. NASA Worm Modernist (1975–1992, technical) — warm red, warm grey, white, black
93. Psychedelic Seventies Corporate (1968–1979, groovy-but-wholesome) — burnt orange, avocado, harvest gold
94. Y2K Chrome (1998–2004, optimistic) — silver, electric blue, hot pink, white
95. Contemporary Flat Illustration (2015–present, contemporary) — coral, teal, navy
96. Vaporwave (2010–present, nostalgic) — pastel neon — cyan, lavender, hot pink
97. Synthwave / Retrowave (2010–present, nostalgic) — deep purple-black, hot pink, electric blue
98. Glitch Art (2005–present, uncanny) — base image fragmented, RGB shifts
99. Pixel Art (1978–present, nostalgic) — hardware-specific palettes — CGA, EGA, NES
100. Low Poly (1994–present, uncanny) — flat unshaded polygon colours, vivid

[TYPOGRAPHY — title and artist are SEPARATE wordmark elements]
Title sits at the TOP of the frame as the large display wordmark. Artist sits at the BOTTOM of the frame as a smaller subtitle. They are NEVER concatenated. Each carries a treatment string derived from the chosen Atlas style's WORDMARK/TYPE brief.

JSON SHAPE — output this single object, populated to match the song:

{
  "spec_version": "scene-blueprint-2.0",
  "global_scene": {
    "environment": { "location_type": "indoor|outdoor|vehicle|mixed", "setting_description": "", "time_of_day": "", "weather": { "condition": "clear|overcast|rain|fog|snow|storm|dust|indoors", "intensity": "none|light|medium|heavy" } },
    "lighting": { "overall_mood": "naturalistic|cinematic|noir|high_key|low_key" },
    "camera": { "framing": "wide|medium|close|macro", "angle": "eye_level|low_angle|high_angle|overhead|dutch" },
    "style": {
      "render_type": "photoreal|illustration|graphic|mixed",
      "atlas_id": "<one of 100 Atlas ids, e.g. neon_sign>",
      "atlas_name": "<style name, e.g. Neon Sign / Diner Americana>",
      "traits": "<2–4 trait phrases from Atlas dossier>",
      "references": ["<one or two named refs from Atlas, e.g. Saul Bass>"],
      "palette": "<verbatim Atlas PALETTE line>",
      "mood": "<verbatim Atlas MOOD line>",
      "excludes": ["<Atlas EXCLUDE entries, image-side negatives>"]
    }
  },
  "objects": [],
  "people": [],
  "typography": {
    "enabled": true,
    "title": { "text": "<song title>", "position": "top of frame, centered, large display wordmark", "treatment": "<Atlas WORDMARK/TYPE brief>" },
    "artist": { "text": "<artist name>", "position": "bottom of frame, centered, smaller subtitle weight", "treatment": "<Atlas WORDMARK/TYPE brief, scaled down>" }
  }
}`;

export const MUSIC_CRITIQUE_JSON_INSTRUCTION = `\n\nRespond in JSON only, no markdown wrapping. Use this exact schema:
{
  "songId": "string",
  "timestamp": "string",
  "overallScore": number,
  "sections": {
    "lyricalAnalysis": { "score": number, "feedback": "string", "strengths": ["string"], "weaknesses": ["string"] },
    "musicalArrangement": { "score": number, "feedback": "string", "instrumentationReview": "string", "grooveAnalysis": "string" },
    "production": { "score": number, "feedback": "string", "mixQuality": "string", "sonicClarity": "string" },
    "genreAuthenticity": { "score": number, "feedback": "string", "southernRockElements": ["string"], "authenticityConcerns": ["string"] }
  },
  "improvementSuggestions": ["string"],
  "summaryVerdict": "string"
}`;

export interface SunoPromptInput {
  systemPrompt: string;
  userPrompt: string;
  styleStrength: number;
  weirdness: number;
}

export function buildSunoPromptInput(
  data: HookhouseFormData,
  vocalArchitecture?: string,
  productionArchitecture?: string,
  riffDnaArchitecture?: string,
  soloArchitecture?: string,
  narrativeArchitecture?: string,
  personaOverride?: PersonaOverride | null
): SunoPromptInput {
  const isPersonaActive = !!personaOverride;
  const isPersonaLocked = isPersonaActive && personaOverride!.useLockedTemplate && !!personaOverride!.styleTemplate.trim();
  const isPresetOverride = !isPersonaActive && data.stylePreset && data.stylePreset !== 'None';
  let styleInstruction = `**Musical Blend:** ${data.blends.join(", ")}`;
  if (isPersonaLocked) {
    // Locked persona wins over everything -- same "TOTAL OVERRIDE" strength
    // as a Style Preset override, since a persona always takes precedence
    // over a preset when both are active (see the isPresetOverride guard
    // below) and previously left NOTHING in its place when the persona had
    // no vocalArchitecture/productionArchitecture text -- the actual cause
    // of a locked persona generating a completely unrelated song. See
    // src/shared/personaLock.ts for the deterministic Style-field half of
    // this fix (applied post-generation in llmDispatcher.ts).
    styleInstruction = `**LOCKED PERSONA STYLE PROMPT (TOTAL OVERRIDE — reproduce exactly, tempo/key words aside; do not alter, paraphrase, or invent a different sonic direction):**\n${renderLockedStyleTemplate(personaOverride!)}`;
  } else if (isPresetOverride) {
    const presetDescription = STYLE_PRESETS[data.stylePreset];
    if (presetDescription) styleInstruction = `**Style Preset (TOTAL OVERRIDE):**\n${presetDescription}\n\nSTYLE/TAGS RULES (NON-NEGOTIABLE):\n1. The style/tags output MUST derive EXCLUSIVELY from the Style Preset above — genre, sub-genre, instruments, timbres, voice, key, BPM, and production descriptors.\n2. IGNORE all timbral and genre descriptors from any Riff DNA, MOOD, THEMES, INSTRUMENTS, or CONSTRAINTS fields below. Those fields drive LYRICS and NARRATIVE only — they MUST NOT contribute words to the style/tags output.\n3. If Riff DNA below describes heavy/distorted/doom/metal/etc. timbres that conflict with the preset, RE-SKIN the riff's rhythmic and structural intent through the preset's sonic palette. Keep the rhythm pattern; drop the conflicting timbre words.\n4. Do NOT add instruments to the style/tags that are not named in the preset. Do NOT invent extra production gear (preamps, mics, reverbs, etc.) beyond what the preset specifies.`;
  }

  let vocalPackInstruction = "";
  let productionInstruction = "";
  let bandIdentityInstruction = "";

  if (data.isInstrumental) {
    vocalPackInstruction = "\n**INSTRUMENTAL MODE:** No vocals. Pure instrumental composition. Do not generate any vocal lines, lyrics, choir, or sung content. All melodic and emotional content carried by instruments.";
    productionInstruction = !isPresetOverride && productionArchitecture ? `\n**Production Architecture & Multi-Layer Physics Filter:**\n${productionArchitecture}` : "";
  } else if (isPersonaActive) {
    const vocalParts: string[] = [];
    if (personaOverride!.vocalArchitecture) vocalParts.push(`**Vocal Architecture (Persona):**\n${personaOverride!.vocalArchitecture}`);
    if (personaOverride!.vocalPhysicsLocked) vocalParts.push(`**Vocal Physics (Persona — MUST NOT CHANGE, reproduce this delivery exactly):**\n${personaOverride!.vocalPhysicsLocked}`);
    vocalPackInstruction = vocalParts.length ? `\n${vocalParts.join('\n\n')}` : "";

    const prodParts: string[] = [];
    if (personaOverride!.productionArchitecture) prodParts.push(`**Production Architecture (Persona):**\n${personaOverride!.productionArchitecture}`);
    if (personaOverride!.rhythmPhysicsGuidance) prodParts.push(`**Rhythm Section Physics (Persona — changeable within the genre, do not contradict):**\n${personaOverride!.rhythmPhysicsGuidance}`);
    productionInstruction = prodParts.length ? `\n${prodParts.join('\n\n')}` : "";

    bandIdentityInstruction = personaOverride!.bandIdentityBlock ? `\n${personaOverride!.bandIdentityBlock}\n` : "";
  } else {
    vocalPackInstruction = !isPresetOverride && vocalArchitecture ? `\n**Vocal Architecture:**\n${vocalArchitecture}\n[VOCAL TAGS ARE PRE-FILLED — the META_STYLES and META_EXCLUDE tags above are inserted verbatim at the front of SUNO TAGS and EXCLUDED STYLES automatically after you respond. Do NOT write your own lead-vocal descriptors (voice type, range, timbre, texture, grit/rasp, gender) in SUNO TAGS — a paraphrase competes with the profile and drags the voice back to a generic default. Spend SUNO TAGS on genre, instrumentation, production, and arrangement.]` : "";
    productionInstruction = !isPresetOverride && productionArchitecture ? `\n**Production Architecture & Multi-Layer Physics Filter:**\n${productionArchitecture}` : "";
  }

  let riffDnaInstruction = "";
  if (riffDnaArchitecture) {
    const riffPresetGuard = isPresetOverride
      ? `\n[STYLE PRESET ACTIVE — the riff guidance below provides RHYTHM & STRUCTURE only. Recast all timbral/genre descriptors through the preset's sonic palette. Do NOT let riff timbre words (heavy, distorted, doom, chugging, crushing, etc.) leak into the style/tags output.]\n`
      : "";
    riffDnaInstruction = `\n${riffPresetGuard}${riffDnaArchitecture}\n`;
  }

  // Suno reads ANY appearance of "live" in style/tags as an instruction to
  // produce a live-concert take (crowd noise, audience atmosphere) -- even
  // innocuous studio-engineering uses like "live room feel". Default to
  // forbidding it outright; only 'live' mode wants that character.
  const styleModeInstruction = data.styleMode === 'live'
    ? `\n[LIVE MODE — CONCERT PERFORMANCE]\nThis track should read as a live concert performance — crowd presence, audience energy, and live-room atmosphere are all wanted. Feel free to use "live" language in the style/tags output.`
    : `\n[STUDIO MODE — LIVE LANGUAGE FORBIDDEN]\nDo NOT use the word "live" anywhere in the SUNO TAGS or EXCLUDED STYLES output, even in legitimate studio-engineering senses (e.g. "tracked live in the room" meaning musicians played together simultaneously). Suno reads ANY appearance of "live" as an instruction to produce a live-concert performance take, which is wrong for a studio recording. If source material describes simultaneous/in-room tracking using the word "live," rephrase it without that word: "tracked together in the room," "played simultaneously," "captured in one take," "room-mic'd ensemble take" — never "live."`;

  let soloInstruction = "";
  if (soloArchitecture) {
    soloInstruction = `\n[SOLO CHOREOGRAPHY INSTRUCTIONS]\nThe song structure includes a solo section. When you reach the solo, use EXACTLY this header and choreography:\n\n${soloArchitecture}\n\nDo NOT improvise or change the solo choreography. Use it exactly as provided above. The solo should appear after the Bridge section in the song structure.\n[END SOLO INSTRUCTIONS]`;
  }

  let narrativeInstruction = "";
  if (narrativeArchitecture) {
    narrativeInstruction = `\n${narrativeArchitecture}\n`;
  }

  const structureInstructionText = buildStructureInstructionText(data.songStructure);
  const rhymeInstructionText = buildRhymeInstructionText(data.rhymeScheme);

  const userPrompt = `ARCHITECT SONG BLUEPRINT
SEED: "${data.seed}"
${styleInstruction}
${styleModeInstruction}
${vocalPackInstruction}
${productionInstruction}
${bandIdentityInstruction}
${riffDnaInstruction}
${soloInstruction}
${narrativeInstruction}
[SPECIAL INSTRUCTIONS FOR PHYSICS LAYERS]
You MUST incorporate all provided physics layers into the LYRICS block:
- {LAYER-1 VOCAL PHYSICS} MUST be at the absolute top of the lyrics.
- {LAYER-2 RHYTHM SECTION PHYSICS} should be placed before the first verse or intro.
- {LAYER-3 GUITAR PHYSICS} should be placed before the first solo or heavy instrumental section.
- {LAYER-4 ATMOSPHERE PHYSICS} and {LAYER-5 SPECIALTY PHYSICS} (if provided) should be placed before sections where they become prominent.
- {LAYER-6 SOLO CHOREOGRAPHY} (if provided) MUST be used for the instrumental solo section.

MOOD: ${data.mood.join(", ")}
EXPLICIT: ${data.explicit}
POV/SETTING: ${data.povSetting}
[POV/SETTING is creative direction, not source text — even when it reads like a lyric (e.g. an album-level concept statement). Do NOT quote, closely paraphrase, or reuse specific lines/phrases from it in the lyrics. Every lyric line must be original wording written for THIS song. This applies doubly on multi-track albums: if the same POV/SETTING or ALBUM NARRATIVE text is supplied across several songs, each song still needs a wholly distinct opening line and verse — converging on the same phrasing is a failure, not coherence.]
THEMES: ${data.themesToHit}
AVOID: ${data.themesToAvoid}
INSTRUMENTS: ${data.instruments}
CONSTRAINTS: ${data.constraints}
STYLE_STRENGTH_TARGET: ${data.styleStrength}
WEIRDNESS_TARGET: ${data.weirdness}${structureInstructionText ? `\n${structureInstructionText}` : ''}${rhymeInstructionText ? `\n${rhymeInstructionText}` : ''}${isPresetOverride ? `\n\n[STYLE-TAGS REMINDER: The MOOD / THEMES / INSTRUMENTS / CONSTRAINTS above inform LYRICS and NARRATIVE only. The style/tags output derives EXCLUSIVELY from the Style Preset at the top of this prompt.]` : ''}

[STRUCTURAL DISCIPLINE — Studio Team v2.5 distilled]
Apply these disciplines to THIS song without walking the agent roster. The artifacts (Fibonacci structure, anti-hum tags) must appear in the output even though no agent walkthrough is produced.

PHI / Fibonacci structural math — apply by default, DECLINE only when genre fights it:
- Every section bar count is a Fibonacci number (1, 2, 3, 5, 8, 13, 21, 34). No 4-bar pre-chorus or 16-bar verse.
- Total bars = N. Phi point P = round(N × 0.618). Bar P MUST land INSIDE a section, ≥2 bars in — never at a section boundary.
- The pivot (the one truth-revelation lyric line — the song's emotional inversion) lands ON or within 1 bar of P. Mark the section: [Bridge — Phi Moment ~0.618] (or [Climax — Phi Moment ~0.618] when there's no bridge).
- Aftermath check: post-phi bar count A. A / N must sit between 0.30 and 0.42 (between roughly a third and just under half).
- DECLINE STATE: if radio pop / strict EDM / sub-90-second cuts make Fibonacci wrong for this brief, add the bracketed line "[Structure: STANDARD ARCHITECTURE PRESERVED — one-sentence reason]" to the STYLE (PRODUCTION BLUEPRINT) block (never the SUMMARY block — see below) and use a conventional 4/8/16-bar grid. Do NOT pretend Fibonacci was applied.
- Suno V5 renders bar counts approximately. You are expressing structural INTENT, not arithmetic. A 76-bar plan rendering as 75 bars is a pass.

[SUMMARY BLOCK IS NARRATIVE ONLY — NON-NEGOTIABLE]
Everything above (Fibonacci bar math, Phi point, aftermath ratio, the Anti-Hum protocol below) is internal discipline for building the LYRICS and STYLE blocks. NONE of it belongs in the SUMMARY block. ${SUMMARY_RULE}

Anti-Hum protocol — vocal songs only (skip for instrumentals). Apply before finalizing lyrics:
1. Terminal phoneme of EVERY line-final word is a hard consonant (B/D/G/K/P/T/hard S) or a short clipped vowel — NOT an open vowel (AH/AW/OH/OW), trailing nasal hum (M/N/NG hanging alone), or a long-held EE/AY. Open line-endings invite Suno to hum into the gap.
2. No bare gaps between sections — every section header is followed immediately by the next vocal cue or [Vocal: ...] tag from the §4.3 bank. No empty lines after a section header.
3. The bleed token ///*****/// sits between the physics-layer metadata block and the lyrics proper so Suno doesn't sing meta-tags.
4. No mid-line ellipses ("…") or trailing em-dashes ("—") that hang an open vowel mid-bar.
5. No section ends on an unsupported syllable ("yeah", "ohh", "ahh", "mmm", "la") — those become Suno hums by default. If you need a vowel-end for rhyme, follow it with a hard-consonant anchor word ("set", "down", "stop", "go", "right", "back", "gone").
6. Every [Vocal: ...] tag is for a sung section; instrumental breaks use [Instrumental] tags instead — never leave a [Vocal:] tag with no lyrics under it.
7. Bridge-to-Final-Chorus transitions specifically: the final word of the bridge MUST land on a plosive (B/D/G/K/P/T) or hard fricative (F/S/Z). This is the single most common Suno hum-bleed.
8. Final outro line ends on a hard consonant followed by [end], not on a held vowel + fade.

[SUNO TAGS / EXCLUDED STYLES ARE SOUND ONLY]
Both fields describe what the record SOUNDS like (or must not sound like). Lyric themes, story imagery, narrative concepts, and subject matter (e.g. "mask and truth", "workplace complaint", "desert transition") NEVER go in either field — Suno can't hear a theme, and every such tag wastes the character budget. Themes belong in LYRICS and SUMMARY only.

[HARD CHARACTER LIMITS — MANDATORY]
These are Suno V5 platform limits. Exceeding them means the output CANNOT be used.
- SUNO TAGS: MAXIMUM 1,000 characters. Count carefully. Be concise with tags.
- EXCLUDED STYLES: MAXIMUM 1,000 characters.
- LYRICS (including all physics layers, section tags, and text): MAXIMUM 5,000 characters total.
  - Write SHORT, punchy verses (4 lines each, ~25 words per line MAX).
  - Do NOT pad with excessive physics descriptions or verbose metatags.
  - If you are approaching the limit, CUT the Bridge or reduce verses. NEVER exceed 5,000 characters.

[STRUCTURE-vs-LIMITS TIE-BREAKER — non-negotiable]
The 5,000-char lyrics ceiling OVERRIDES the Fibonacci section preference. If your planned PHI structure (especially with 13/21/34-bar sections) would push the lyrics block over 5,000 chars, you MUST shrink BEFORE drafting:
- First: drop to shorter Fibonacci numbers — replace any 21-bar with 13, any 13-bar with 8, any 8-bar with 5 — recompute N, P = round(N × 0.618), and the aftermath ratio at the new total.
- If still over: drop one full section (Verse 3 first, then Pre-Chorus 2, then Bridge — never the Chorus).
- If still over: tighten verses to 3 lines each, ≤18 words per line.
- A song with a smaller-Fibonacci structure that fits under 5,000 chars is a PASS. A song with the "ideal" larger Fibonacci that overruns is a HARD REJECT.
This is a HARD REJECT if any section exceeds its limit. Brevity is non-negotiable.`;

  return {
    systemPrompt: HOOKHOUSE_PRO_SYSTEM_PROMPT,
    userPrompt,
    styleStrength: data.styleStrength,
    weirdness: data.weirdness,
  };
}

// ============================================
// Lyrics hard-cap enforcement (5,000 chars)
// ============================================
//
// The HARD CHARACTER LIMITS instruction in the song prompt is not reliably
// respected by any provider — Claude and Gemini both overrun by 1-2K when
// the PHI Fibonacci structure pushes verses long. This is a deterministic
// post-generation trim pass: if parsed lyrics > 5000 chars, ask the same
// provider to trim the LYRICS block only while preserving structure, then
// splice the result back in. Mirrors the pattern already in use on the
// server-side Studio Team route (server/routes/studioteam.routes.ts).
//
// Cost: one extra LLM call ONLY when over the cap. Most generations skip it.

const LYRICS_HARD_MAX = 5000;
const LYRICS_TRIM_TARGET = 4700; // aim well under so the model has a margin

/**
 * Uniform callback signature each provider service supplies to drive the
 * trim pass with its own client. Tokens are optional for callers that don't
 * track them on the trim pass.
 */
export type LyricsTrimCallback = (
  systemPrompt: string,
  userPrompt: string,
  temperature: number,
  maxTokens: number,
) => Promise<{ text: string; inputTokens?: number; outputTokens?: number }>;

/**
 * If the parsed output's lyrics block exceeds 5,000 chars, run a single
 * trim pass through the supplied provider and return an updated parsed
 * object. On any failure (no fenced block in reply, still over, suspiciously
 * short, network error) we keep the original — better to ship slightly-long
 * lyrics than to silently produce broken output. The Suno injection script
 * already truncates at 5,000 as a final safety net.
 */
export async function enforceLyricsLimit(
  parsed: ParsedSunoOutput,
  callLlm: LyricsTrimCallback,
): Promise<ParsedSunoOutput> {
  const original = parsed.lyricsWithTags || '';
  const originalLen = original.length;
  if (originalLen <= LYRICS_HARD_MAX) return parsed;

  console.warn(`[lyrics-trim] Lyrics ${originalLen} > 5000 — running trim pass…`);

  const trimSystem = `You are an expert lyric editor working under hard platform limits. Output ONLY the trimmed LYRICS block inside a single triple-backtick code fence. No commentary, no other sections, no markdown headings.`;

  const trimUser = `The lyrics block below is ${originalLen} chars. Suno V5's hard ceiling is 5,000. TRIM to approximately ${LYRICS_TRIM_TARGET} chars (must finish under 4,900).

Preserve ALL of:
- Every [Section] tag (Intro, Verse, Pre-Chorus, Chorus, Bridge, Outro, etc.) — same count, same order
- Every {LAYER-N PHYSICS} block — verbatim, do not edit the physics layer content
- Every (parenthetical) and [bracketed] inflection / emotion marker
- The chorus hook line — verbatim, same number of repeats per chorus
- The Emotional Pivot line (if marked or identifiable in the bridge / climax)
- The metadata bleed token  ///*****///  if present

Cut by tightening redundant lines, dropping filler in Verse 2 first, shortening Pre-Chorus 2 if it ran long, reducing repeated outro tags. Do NOT remove whole sections. Do NOT remove section tags or physics layers. Do NOT exceed 5,000 chars under any circumstances.

Return ONLY the corrected LYRICS block wrapped in a single triple-backtick code fence. No prose before or after.

CURRENT LYRICS BLOCK:
\`\`\`
${original}
\`\`\``;

  try {
    const { text } = await callLlm(trimSystem, trimUser, 0.5, 4000);
    const fence = text.match(/```(?:[a-zA-Z]+)?\s*\n([\s\S]*?)```/);
    if (!fence) {
      console.warn('[lyrics-trim] No fenced block in trim reply; keeping original');
      return parsed;
    }
    const trimmed = fence[1].trim();
    if (trimmed.length > LYRICS_HARD_MAX) {
      console.warn(`[lyrics-trim] Trim still ${trimmed.length} chars > 5000; keeping original (Suno injection will hard-truncate)`);
      return parsed;
    }
    if (trimmed.length < 1500) {
      console.warn(`[lyrics-trim] Trim suspiciously short (${trimmed.length}); keeping original`);
      return parsed;
    }
    console.log(`[lyrics-trim] ${originalLen} → ${trimmed.length} chars`);
    return {
      ...parsed,
      lyricsWithTags: trimmed,
      // Recompute the lyrics-only view the same way parseResponse does
      lyricsAlone: trimmed.replace(/\{LAYER-1 VOCAL PHYSICS\}[\s\S]*?\n\n/g, '').replace(/\[.*?\]\n?/g, '').trim(),
      charLimits: parsed.charLimits ? {
        ...parsed.charLimits,
        lyrics: { count: trimmed.length, max: LYRICS_HARD_MAX, over: false },
      } : parsed.charLimits,
    };
  } catch (err) {
    console.warn('[lyrics-trim] Trim pass failed:', err instanceof Error ? err.message : err);
    return parsed;
  }
}

export interface RegenerateInput {
  systemPrompt: string;
  userPrompt: string;
  styleStrength: number;
  weirdness: number;
}

export interface RegenerateOptions {
  /** True when the dispatcher will put the vocal profile's own tags at the front
   *  of SUNO TAGS afterwards (see src/shared/vocalIdentity.ts). */
  vocalTagsPrefilled?: boolean;
}

export function buildRegenerateInput(originalOutput: ParsedSunoOutput, critique: MusicCritique, opts?: RegenerateOptions): RegenerateInput {
  const currentTitle = originalOutput.title;
  const versionMatch = currentTitle.match(/\[V(\d+)\]/);
  const nextVersion = versionMatch ? parseInt(versionMatch[1]) + 1 : 1;
  const baseTitle = currentTitle.replace(/\s*\[V\d+\]/, '').trim();
  const newTitle = `${baseTitle} [V${nextVersion}]`;

  const userPrompt = `REVISION REQUEST FOR: ${originalOutput.titleArtist}\nNEW TITLE TARGET: ${newTitle}\n\nORIGINAL DATA:\nSUMMARY: ${originalOutput.summary}\nSTYLE BLUEPRINT: ${originalOutput.styleBlueprint}\nLYRICS: ${originalOutput.lyricsWithTags}\n\nBIG CHUCK'S CRITICAL ADVICE:\n${critique.improvementSuggestions.join('\n')}${opts?.vocalTagsPrefilled ? `\n\n[VOCAL TAGS ARE PRE-FILLED — the lead vocal's identity tags are inserted verbatim at the front of SUNO TAGS automatically after you respond. Do NOT write your own lead-vocal descriptors (voice type, range, timbre, texture, grit/rasp, gender) in SUNO TAGS — a paraphrase competes with the profile and drags the voice back to a generic default. Spend SUNO TAGS on genre, instrumentation, production, and arrangement.]` : ''}\n\nTASK: Regenerate following Hookhouse Pro format. Ensure all physics layers {LAYER-1} through {LAYER-6} are preserved and correctly positioned in the lyrics.\n\n[HARD CHARACTER LIMITS — MANDATORY]\nSUNO TAGS: MAX 1,000 chars. EXCLUDED STYLES: MAX 1,000 chars. LYRICS: MAX 5,000 chars total (including physics layers and section tags). Do NOT exceed these limits under any circumstances.`;

  return {
    systemPrompt: HOOKHOUSE_PRO_SYSTEM_PROMPT,
    userPrompt,
    styleStrength: originalOutput.styleStrength || 85,
    weirdness: originalOutput.weirdness || 65,
  };
}

export function buildImagePrompt(output: ParsedSunoOutput): { systemPrompt: string; userPrompt: string } {
  return {
    systemPrompt: IMAGE_BLUEPRINT_SYSTEM_PROMPT,
    userPrompt: `Based on song data, generate a high-fidelity image blueprint in JSON format.\n\nTITLE: ${output.title}\nARTIST: ${output.artist}\nSUMMARY: ${output.summary}\nPRODUCTION STYLE: ${output.styleBlueprint}\nLYRICS: ${output.lyricsAlone.substring(0, 1000)}`,
  };
}

export function buildCritiquePrompt(clip: SunoClip): { systemPrompt: string; userPrompt: string } {
  const styleInfo = clip.originalData?.styleBlueprint || clip.metadata.tags || 'N/A';
  const lyricsInfo = clip.originalData?.lyricsWithTags || clip.metadata.prompt || 'N/A';
  const summaryInfo = clip.originalData?.summary || 'N/A';

  return {
    systemPrompt: MUSIC_CRITIC_SYSTEM_PROMPT,
    userPrompt: `Analyze this song architecture and provide a detailed JSON critique. You MUST provide real scores and meaningful feedback for every section.

  TITLE: ${clip.title}
  PRODUCTION ARCHITECTURE: ${styleInfo}
  LYRICAL ARCHITECTURE: ${lyricsInfo}
  NARRATIVE SUMMARY: ${summaryInfo}`,
  };
}

/**
 * Parse critique JSON from any provider's text response.
 * Handles markdown wrapping and fills in songId/timestamp.
 */
export function parseCritiqueResponse(responseText: string, clipId: string): MusicCritique {
  let text = responseText.trim();
  text = text.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '');

  const jsonMatch = text.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new Error("Failed to parse critique response as JSON");
  }

  const parsed = JSON.parse(jsonMatch[0]) as MusicCritique;
  return {
    ...parsed,
    songId: clipId,
    timestamp: new Date().toISOString()
  };
}
